import { Head } from '@inertiajs/react'
import {
  AlertTriangle,
  ArrowRight,
  Check,
  Sparkles,
  TrendingUp,
} from 'lucide-react'
import type { ReactNode } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { AppLayout } from '@/layouts/AppLayout'

/* -------------------------------------------------------------------------
 * Brand reference page.
 *
 * This page is the living style guide for the Platanus theme. Every example
 * here uses the same CSS variables (--background, --primary, --highlight, …)
 * that the rest of the app uses, so the page will always reflect the
 * current theme in both light and dark mode.
 *
 * Use the header theme toggle to preview both modes.
 * ---------------------------------------------------------------------- */

function Section({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  children: ReactNode
}) {
  return (
    <section className="mb-14">
      <header className="mb-5">
        <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          {title}
        </h2>
        {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
      </header>
      {children}
    </section>
  )
}

function TokenRow({ name, description }: { name: string; description: string }) {
  return (
    <div className="flex items-center gap-4 py-2">
      <div
        className="h-8 w-8 shrink-0 rounded-md border border-border"
        style={{ backgroundColor: `var(--${name})` }}
      />
      <div className="min-w-0 flex-1">
        <code className="text-xs font-mono text-foreground">--{name}</code>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
    </div>
  )
}

function Brand() {
  return (
    <>
      <Head title="Brand — Platanus Track" />

      <div className="mx-auto max-w-5xl px-4 py-12">
        <div className="mb-12">
          <Badge variant="highlight" className="mb-3">
            <Sparkles className="mr-1 h-3 w-3" />
            Platanus
          </Badge>
          <h1 className="text-4xl font-semibold tracking-tight">Brand</h1>
          <p className="mt-3 max-w-2xl text-muted-foreground">
            Living style guide for the Platanus theme. Colors are driven by CSS variables
            in <code className="rounded bg-muted px-1.5 py-0.5 text-xs">application.css</code>{' '}
            and swap automatically in dark mode via the{' '}
            <code className="rounded bg-muted px-1.5 py-0.5 text-xs">.dark</code> class on{' '}
            <code className="rounded bg-muted px-1.5 py-0.5 text-xs">&lt;html&gt;</code>.
          </p>
        </div>

        <Section
          title="Typography"
          description="Inter var. Foreground on background. Headings are tight, body is relaxed."
        >
          <Card>
            <CardContent className="space-y-3 p-6">
              <h1 className="text-4xl font-semibold tracking-tight">The quick brown fox</h1>
              <h2 className="text-2xl font-semibold tracking-tight">The quick brown fox</h2>
              <h3 className="text-lg font-semibold">The quick brown fox</h3>
              <p className="text-base leading-relaxed">
                Body copy uses the default foreground token. Try selecting this text — the
                selection color uses <code className="rounded bg-muted px-1 text-xs">--highlight</code>.
                Search matches and <mark>marked text</mark> use the same token so they feel
                unified across the product.
              </p>
              <p className="text-sm text-muted-foreground">
                Muted paragraph for secondary information, timestamps, footnotes.
              </p>
            </CardContent>
          </Card>
        </Section>

        <Section
          title="Color tokens"
          description="All tokens adjust automatically between light and dark mode."
        >
          <Card>
            <CardContent className="grid gap-x-8 gap-y-1 p-6 sm:grid-cols-2">
              <TokenRow name="background" description="Page surface" />
              <TokenRow name="foreground" description="Body text" />
              <TokenRow name="primary" description="Primary CTA" />
              <TokenRow name="primary-foreground" description="Text on primary" />
              <TokenRow name="accent" description="Warm amber accent" />
              <TokenRow name="accent-foreground" description="Text on accent" />
              <TokenRow name="highlight" description="Marked text, ::selection" />
              <TokenRow name="highlight-foreground" description="Text on highlight" />
              <TokenRow name="muted" description="Subdued fill" />
              <TokenRow name="muted-foreground" description="Secondary copy" />
              <TokenRow name="destructive" description="Errors, delete" />
              <TokenRow name="border" description="Outlines, dividers" />
            </CardContent>
          </Card>
        </Section>

        <Section title="Buttons" description="variant: default, secondary, outline, ghost, destructive, link.">
          <Card>
            <CardContent className="flex flex-wrap items-center gap-3 p-6">
              <Button>
                Primary
                <ArrowRight />
              </Button>
              <Button variant="secondary">Secondary</Button>
              <Button variant="outline">Outline</Button>
              <Button variant="ghost">Ghost</Button>
              <Button variant="destructive">
                <AlertTriangle />
                Destructive
              </Button>
              <Button variant="link">Link style</Button>
              <Separator orientation="vertical" className="mx-2 h-8" />
              <Button size="sm">Small</Button>
              <Button size="lg">Large</Button>
              <Button disabled>Disabled</Button>
            </CardContent>
          </Card>
        </Section>

        <Section title="Badges">
          <Card>
            <CardContent className="flex flex-wrap items-center gap-3 p-6">
              <Badge>Default</Badge>
              <Badge variant="secondary">Secondary</Badge>
              <Badge variant="accent">Accent</Badge>
              <Badge variant="highlight">Highlight</Badge>
              <Badge variant="outline">Outline</Badge>
              <Badge variant="destructive">Destructive</Badge>
            </CardContent>
          </Card>
        </Section>

        <Section
          title="Forms"
          description="Inputs, labels and helper text use theme tokens. Focus ring is --ring."
        >
          <Card>
            <CardContent className="grid gap-4 p-6 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="fund-name">Fund name</Label>
                <Input id="fund-name" defaultValue="Atlas Pre-Seed Fund I" />
                <p className="text-xs text-muted-foreground">Visible to LPs in reports.</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="fund-size">Fund size</Label>
                <Input id="fund-size" defaultValue="$12,480,302" />
                <p className="text-xs text-muted-foreground">USD.</p>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="fund-notes">Notes</Label>
                <Input id="fund-notes" placeholder="Drafting thesis…" />
              </div>
            </CardContent>
            <CardFooter className="flex justify-end gap-2">
              <Button variant="ghost">Cancel</Button>
              <Button>
                <Check />
                Save fund
              </Button>
            </CardFooter>
          </Card>
        </Section>

        <Section
          title="Card patterns"
          description="Composable Card with Header, Content, Footer."
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Assets under management</CardTitle>
                  <Badge variant="accent">
                    <TrendingUp className="mr-1 h-3 w-3" />
                    +4.2%
                  </Badge>
                </div>
                <CardDescription>Across all active vintages.</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-semibold tabular-nums">$12,480,302</p>
                <p className="mt-2 text-sm text-muted-foreground">
                  Investors saw a <mark>12% uplift in realized returns</mark> vs. the prior vintage.
                </p>
              </CardContent>
              <Separator />
              <CardFooter className="pt-4">
                <Button variant="link" className="px-0">
                  View breakdown
                  <ArrowRight />
                </Button>
              </CardFooter>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Deal shapes</CardTitle>
                <CardDescription>Standards for the current cohort.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span>Pre-seed SAFE</span>
                  <Badge>$200k / ~7%</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span>Opportunity</span>
                  <Badge variant="secondary">Variable</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span>Bridge</span>
                  <Badge variant="outline">Case-by-case</Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </Section>

        <Section title="Usage cheat sheet" description="Reach for these utilities first.">
          <Card>
            <CardContent className="grid gap-x-8 gap-y-2 p-6 font-mono text-xs sm:grid-cols-2">
              <div><code>bg-background</code> / <code>text-foreground</code></div>
              <div>Page surface + body text</div>
              <div><code>bg-card</code> / <code>text-card-foreground</code></div>
              <div>Raised cards, popovers</div>
              <div><code>bg-primary</code> / <code>text-primary-foreground</code></div>
              <div>Main CTAs, brand moments</div>
              <div><code>bg-muted</code> / <code>text-muted-foreground</code></div>
              <div>Subdued fills &amp; secondary copy</div>
              <div><code>bg-accent</code> / <code>text-accent-foreground</code></div>
              <div>Amber highlights, promo badges</div>
              <div><code>bg-highlight</code> / <code>&lt;mark&gt;</code> / <code>::selection</code></div>
              <div>Text highlights, search hits</div>
              <div><code>border-border</code></div>
              <div>Dividers, form borders, card outlines</div>
              <div><code>ring-ring</code></div>
              <div>Focus ring on interactive elements</div>
            </CardContent>
          </Card>
        </Section>
      </div>
    </>
  )
}

export default Object.assign(Brand, {
  layout: (page: ReactNode) => <AppLayout>{page}</AppLayout>,
})
